import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { logout } from '../features/auth/authSlice';

/** Backend base URL (BASE=https://chunaav.oogw.ai/api). Trailing slash so endpoint paths join cleanly. */
export const BASE_URL = 'https://chunaav.oogw.ai/api/';

/** Where the auth token lives once login issues one. The token flow is bypassed for now. */
export const TOKEN_STORAGE_KEY = 'chunaav.token';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers) => {
    // Attach the bearer token only when present — requests work unauthenticated until login issues one.
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token && token !== 'undefined') {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

/**
 * The single shared base query. Every feature slice goes through here, so this is
 * the one place to add auth headers, global loaders, or error/session handling.
 */
const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  Record<string, unknown>,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  // An expired/invalid token on any request (except the login attempt itself)
  // clears the session so the app redirects back to /login.
  if (result.error?.status === 401 && api.endpoint !== 'login') {
    api.dispatch(logout());
  }

  return result;
};

/**
 * Root RTK Query API. Feature slices extend it with `baseApi.injectEndpoints(...)`
 * instead of each creating their own `createApi`, so they all share one base query,
 * one reducer, and one cache. Register new cache tags here.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [
    'Organizations',
    'EcScope',
    'OrgLevel',
    'OrgNode',
    'Roles',
    'Permissions',
    'Users',
    'Engagements',
    'Tasks',
    'Comments',
    'ActivityLog',
    'Teams',
  ],
  endpoints: () => ({}),
});
